import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import { SxProps, Theme } from '@mui/material/styles';

import { useConnectRingCentral, useRingCentralConnection } from './useRingCentral';

type Props = {
  sx?: SxProps<Theme>;
};

/**
 * Asks a sender who has not connected RingCentral - or whose connection expired - to connect,
 * before they try to send. Sits at the top of every window a text can be sent from, whatever the
 * mode, so it is seen on opening rather than only once the message turns into a text. Renders
 * nothing once connected; RingCentralFromNumber, under the text editor, takes over from there. The
 * caller disables its Send for texts while `useRingCentralConnection().connected` is false.
 */
export default function RingCentralConnectAlert({ sx }: Props) {
  const { connection, isLoading, connected, expired } = useRingCentralConnection();
  const { connect, connecting } = useConnectRingCentral();

  // Nothing until the status is known, rather than a warning that flashes and goes. An environment
  // with no RingCentral app says so under the text editor instead (RingCentralFromNumber).
  if (isLoading || !connection?.isSetUp || connected) return null;

  return (
    <Alert severity="warning" sx={[{ alignItems: 'center' }, ...(Array.isArray(sx) ? sx : [sx])]}>
      <Stack direction="row" alignItems="center" flexWrap="wrap" columnGap={1.5} rowGap={0.5}>
        {expired
          ? 'Your RingCentral connection has expired. Connect again to send texts.'
          : 'RingCentral needs to be connected to send your texts.'}
        <Button
          variant="contained"
          color="warning"
          size="small"
          onClick={connect}
          disabled={connecting}
        >
          {expired ? 'Reconnect' : 'Connect RingCentral'}
        </Button>
      </Stack>
    </Alert>
  );
}
